import { OPTION_EVENT_KIND_LABELS, OPTION_EVENT_KIND_OBJ, OPTION_RIGHT_OBJ, POSITION_HISTORY_EVENT_TYPE_OBJ, isRecord } from "@signals/shared";
import { formatCurrency } from "../_common";
import type { PositionHistoryOptionEvent, TabRow } from "./portfolio.types";
import { formatPortfolioInstrumentName, isOptionPosition } from "./portfolio-instrument";
import { formatNumber } from "./portfolio-page-formatters";

/** This is an evidence view: no synthetic sale, amount, price or carry-basis calculation. */
export function PortfolioOptionHistoryDetails({ event, displayCurrency: _displayCurrency }: {
  event: PositionHistoryOptionEvent; displayCurrency: string;
}) {
  const exercise = event.optionEventKind === OPTION_EVENT_KIND_OBJ.EXERCISE;
  return <section aria-label="Подтверждённое событие опциона" className="space-y-2 text-sm">
    <p className="font-medium">{formatPortfolioInstrumentName(event)}</p>
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
      <dt>Событие</dt><dd>{OPTION_EVENT_KIND_LABELS[event.optionEventKind]?.["ru-RU"] ?? event.optionEventKind}</dd>
      <dt>Дата</dt><dd>{event.date}</dd>
      <dt>Контракты</dt><dd>{formatNumber(event.quantity)} · множитель {formatNumber(event.multiplier)}</dd>
      <dt>Источник</dt><dd>IBKR · {event.brokerEventId}</dd>
      {event.cashAmount !== null && <><dt>Денежный расчёт</dt><dd>{formatCurrency(event.cashAmount, event.currency)}</dd></>}
      {event.delivery && <><dt>Поставка</dt><dd>{formatNumber(event.delivery.quantity ?? undefined)} · {event.delivery.symbolId ?? "Базовый актив не сопоставлен"}</dd>
        <dt>Оплата по страйку</dt><dd>{event.delivery.currency ? formatCurrency(event.delivery.strikeCashAmount, event.delivery.currency) : "—"}</dd></>}
    </dl>
    {event.optionEventKind === OPTION_EVENT_KIND_OBJ.EXPIRATION && <p>Подтверждённое истечение погашает указанное количество без денежного поступления. Себестоимость погашенной части учитывается в результате; обычной продажи по нулевой цене нет.</p>}
    {exercise && <p>{event.optionContract.right === OPTION_RIGHT_OBJ.CALL
      ? "При исполнении Call премия и комиссия покупки погашенной части переносятся в себестоимость полученных акций. Оплата по страйку учитывается один раз."
      : "При исполнении Put премия погашенной части уменьшает экономическую выручку переданных акций. Фактические деньги по страйку не списываются повторно."}</p>}
    <p className="text-muted-foreground">Событие загружено от брокера и доступно только для просмотра. Версия источника: {event.sourceVersion}.</p>
  </section>;
}

/** Safe row predicate for page actions and the generic SmartTable history cells. */
export function isReadOnlyOptionHistoryRow(row: TabRow): boolean {
  return row["eventType"] === POSITION_HISTORY_EVENT_TYPE_OBJ.OPTION_EVENT || row["readOnly"] === true ||
    typeof row["optionContractId"] === "string" || isOptionPosition({ ...(typeof row["symbolId"] === "string" ? { symbolId: row["symbolId"] } : {}),
      ...(typeof row["optionContractId"] === "string" ? { optionContractId: row["optionContractId"] } : {}) });
}

export function optionHistoryEventLabel(kind: unknown): string | null {
  const found = Object.values(OPTION_EVENT_KIND_OBJ).find((value) => value === kind);
  return found ? OPTION_EVENT_KIND_LABELS[found]?.["ru-RU"] ?? found : null;
}

export function isPositionHistoryOptionEvent(row: TabRow): row is TabRow & PositionHistoryOptionEvent {
  return row["eventType"] === POSITION_HISTORY_EVENT_TYPE_OBJ.OPTION_EVENT && typeof row["id"] === "string" &&
    typeof row["date"] === "string" && typeof row["quantity"] === "number" && typeof row["multiplier"] === "number" &&
    Object.values(OPTION_EVENT_KIND_OBJ).some((kind) => kind === row["optionEventKind"]) &&
    isRecord(row["optionContract"]) && (row["optionContract"]["right"] === OPTION_RIGHT_OBJ.CALL || row["optionContract"]["right"] === OPTION_RIGHT_OBJ.PUT);
}

export function PortfolioOptionHistoryRowDetails({ row, displayCurrency }: { row: TabRow; displayCurrency: string }) {
  if (!isPositionHistoryOptionEvent(row)) return null;
  return <details className="max-w-xl whitespace-normal"><summary className="cursor-pointer">Параметры и завершение</summary>
    <div className="mt-2"><PortfolioOptionHistoryDetails event={row} displayCurrency={displayCurrency} /></div></details>;
}
